import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, History, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { API, errorText } from '../governance_v2/constants';
import { PROJECT_STATUS, SELECT_CLS, fmtDate } from './constants';
import { KanbanBoard } from './KanbanBoard';
import { GanttTimeline } from './GanttTimeline';
import { PredictionPanel } from './PredictionPanel';
import ActivityDialog from './ActivityDialog';
import { CriticalPathPanel } from './CriticalPathPanel';
import { PvAvFPanel } from './PvAvFPanel';

const HistoryTimeline = ({ events }) => (
  <ol className="relative ml-2 space-y-4 border-l border-slate-200 pl-5 dark:border-slate-700" data-testid="history-timeline">
    {events.map((e) => (
      <li key={e.id} className="relative" data-testid={`history-event-${e.type}`}>
        <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white bg-sky-500 dark:border-slate-900" />
        <p className="text-sm text-slate-800 dark:text-slate-200">{e.message}</p>
        <p className="text-[11px] text-slate-500">{new Date(e.at).toLocaleString('es')} · {e.actor}</p>
      </li>
    ))}
    {!events.length && <li className="text-sm text-slate-400">Sin eventos.</li>}
  </ol>
);

const Header = ({ p, companyId, canManage, onBack }) => {
  const qc = useQueryClient();
  const upd = useMutation({
    mutationFn: (status) => axios.put(`${API}/companies/${companyId}/projects/${p.id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', companyId, p.id] }); qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] }); toast.success('Estado actualizado'); },
    onError: (e) => toast.error(errorText(e)),
  });
  const del = useMutation({
    mutationFn: () => axios.delete(`${API}/companies/${companyId}/projects/${p.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] }); toast.success('Proyecto eliminado'); onBack(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-900 dark:hover:text-white" data-testid="project-back-btn"><ArrowLeft className="h-4 w-4" />Proyectos</button>
      <div className="flex flex-wrap items-start gap-4">
        <div className="mr-auto min-w-0">
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="project-detail-title">{p.title}</h3>
          <p className="text-sm text-slate-500">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}{p.description ? ` · ${p.description}` : ''}</p>
          {p.origin?.type === 'proposal' && <p className="mt-1 inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" data-testid="project-origin"><Sparkles className="h-3 w-3" />Nació de una propuesta aprobada{p.origin.agent_name ? ` de ${p.origin.agent_name}` : ''}{p.origin.iad_code ? ` · ${p.origin.iad_code}` : ''}</p>}
        </div>
        <div className="w-44">{canManage ? (
          <select className={SELECT_CLS} value={p.status} onChange={(e) => upd.mutate(e.target.value)} data-testid="project-status-select">
            {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
        ) : <Badge className={PROJECT_STATUS[p.status].cls}>{PROJECT_STATUS[p.status].label}</Badge>}</div>
        {canManage && <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-600" onClick={() => window.confirm('¿Eliminar el proyecto y todo su historial?') && del.mutate()} data-testid="project-delete-btn"><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 rounded-full bg-slate-200 dark:bg-slate-800"><div className="h-2 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-[width] duration-500" style={{ width: `${p.progress}%` }} /></div>
        <span className="text-sm font-semibold" data-testid="project-detail-progress">{p.progress}%</span>
        <span className="text-xs text-slate-500">{p.completed_count}/{p.activity_count} actividades</span>
      </div>
    </div>
  );
};

export const ProjectDetail = ({ companyId, projectId, canManage, currentUserId, onBack }) => {
  const [dialog, setDialog] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['project', companyId, projectId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/projects/${projectId}`)).data,
  });
  const { data: engines } = useQuery({
    queryKey: ['project', companyId, projectId, 'engines'],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/projects/${projectId}/engines`)).data,
  });
  if (isLoading || !data) return <p className="text-sm text-slate-400">Cargando proyecto…</p>;
  const { project, activities, members, events } = data;
  const open = (a) => setDialog({ id: a.id });
  const dialogActivity = dialog?.id ? activities.find((a) => a.id === dialog.id) : null;
  const staffMode = (a) => !canManage && a?.assignee_user_id === currentUserId;
  return (
    <div className="space-y-6" data-testid="project-detail">
      <Header p={project} companyId={companyId} canManage={canManage} onBack={onBack} />
      <Tabs defaultValue="kanban">
        <div className="flex flex-wrap items-center gap-3">
          <TabsList>
            <TabsTrigger value="kanban" data-testid="project-tab-kanban">Kanban</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="project-tab-timeline">Línea de tiempo</TabsTrigger>
            <TabsTrigger value="cpm" data-testid="project-tab-cpm">Ruta crítica</TabsTrigger>
            <TabsTrigger value="pvavf" data-testid="project-tab-pvavf">Plan vs Actual vs Forecast</TabsTrigger>
            <TabsTrigger value="prediction" data-testid="project-tab-prediction">Predicción vs Realidad</TabsTrigger>
            <TabsTrigger value="history" data-testid="project-tab-history"><History className="mr-1 h-3.5 w-3.5" />Historial</TabsTrigger>
          </TabsList>
          {canManage && <Button size="sm" className="ml-auto" onClick={() => setDialog({ id: null })} data-testid="activity-create-btn"><Plus className="mr-1 h-4 w-4" />Nueva actividad</Button>}
        </div>
        <TabsContent value="kanban" className="mt-4"><KanbanBoard companyId={companyId} projectId={projectId} activities={activities} members={members} canManage={canManage} currentUserId={currentUserId} onOpen={open} /></TabsContent>
        <TabsContent value="timeline" className="mt-4"><GanttTimeline activities={activities} members={members} onOpen={open} /></TabsContent>
        <TabsContent value="cpm" className="mt-4">{engines ? <CriticalPathPanel cpm={engines.cpm} activities={activities.filter((a) => engines.cpm.activities[a.id])} members={members} onOpen={open} /> : <p className="text-sm text-slate-400">Calculando…</p>}</TabsContent>
        <TabsContent value="pvavf" className="mt-4">{engines ? <PvAvFPanel engines={engines} /> : <p className="text-sm text-slate-400">Calculando…</p>}</TabsContent>
        <TabsContent value="prediction" className="mt-4"><PredictionPanel project={project} companyId={companyId} canManage={canManage} /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTimeline events={events} /></TabsContent>
      </Tabs>
      {dialog && (dialog.id === null || dialogActivity) && (canManage || staffMode(dialogActivity)) && (
        <ActivityDialog companyId={companyId} projectId={projectId} activity={dialogActivity} activities={activities}
          members={members} staffMode={!canManage} onClose={() => setDialog(null)} />
      )}
    </div>
  );
};
